import type { User } from '@/api/auth';
import {
  ALL_CRYPTO,
  amountWithCurrencySymbol,
  CurrencyCode,
} from '@/helpers/currency';
import { isFrenchLocale } from '@/helpers/languages';
import {
  PERCENT_BREAKPOINTS,
  type TProgressBreakpoint,
} from '@/modules/promotions/helpers/common';
import {
  type ILevelConfig,
  type ILevelItem,
  type LevelReward,
  LevelState,
} from './types';

const clampToBreakpoint = (
  percent: TProgressBreakpoint | -1
): TProgressBreakpoint => (percent === -1 ? 0 : percent);

const getLevelAmounts = (levelConfig: ILevelConfig) =>
  levelConfig.promoRewardType === 'deps'
    ? levelConfig.depsCount
    : levelConfig.wagerAmounts;

// Breakpoints a level actually defines - a level can skip intermediate ones
// (e.g. only 0/25/75/100), so this is not always the full PERCENT_BREAKPOINTS list.
const getLevelBreakpoints = (
  levelConfig: ILevelConfig
): TProgressBreakpoint[] => {
  const amounts = getLevelAmounts(levelConfig);
  return PERCENT_BREAKPOINTS.filter(
    (breakpoint) => amounts[breakpoint] !== undefined
  );
};

const getProgressAmount = (
  levelConfig: ILevelConfig,
  percent: TProgressBreakpoint
) => getLevelAmounts(levelConfig)[percent] ?? 0;

const getLevelTargetAmount = (levelConfig: ILevelConfig) =>
  getLevelAmounts(levelConfig)[100];

const formatAmount = (amount: number) =>
  amount >= 10_000 ? amount.toLocaleString('en-US') : amount;

const formatCurrencyAmount = (amount: number, currencyCode?: string) =>
  amountWithCurrencySymbol(currencyCode, formatAmount(amount), {
    wholeRounding: true,
  });

const formatWageredAmount = (amount: number, currencyCode?: string) =>
  amountWithCurrencySymbol(currencyCode, formatAmount(amount), {
    wholeRounding: true,
    spaced: true,
  });

const formatProgressText = (
  levelConfig: ILevelConfig,
  wagered: number,
  target: number,
  currencyCode?: string
) =>
  levelConfig.promoRewardType === 'deps'
    ? `${formatAmount(wagered)} / ${formatAmount(target)}`
    : `${formatWageredAmount(wagered, currencyCode)} / ${formatAmount(target)}`;

// Levels track wagering/rewards in fiat - crypto wallets fall back to USD.
const normalizeCurrencyCode = (currencyCode?: string) =>
  currencyCode && ALL_CRYPTO.includes(currencyCode as CurrencyCode)
    ? CurrencyCode.USD
    : currencyCode;

const formatRewardLabel = (
  reward: LevelReward,
  currencyCode?: string,
  locale?: string
) =>
  reward.kind === 'freespins'
    ? `${reward.count} ${isFrenchLocale(locale ?? '') ? 'TG' : 'FS'}`
    : formatCurrencyAmount(reward.amount, currencyCode ?? CurrencyCode.USD);

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Group id separators aren't consistent across promo backends (`_`, space, or
// mixed within a multi-word name) - try every combination instead of guessing one.
const toFlexibleSeparatorPattern = (name: string) =>
  escapeRegExp(name).replace(/ /g, '[ _]');

// Breakpoint must be followed by a non-digit (or the end) so a "10" pattern
// can't match inside "100" - the ids have no other delimiter to anchor on.
const BREAKPOINT_END = '(?:$|[^0-9])';

const getGroupIdPattern = (
  groupPrefix: string | undefined,
  useFullNameAsGroupId: boolean,
  levelConfig: ILevelConfig,
  breakpoint: TProgressBreakpoint
) => {
  const namePattern = toFlexibleSeparatorPattern(levelConfig.name);
  return useFullNameAsGroupId
    ? new RegExp(`^${namePattern}[ _]${breakpoint}${BREAKPOINT_END}`)
    : new RegExp(
        `^${groupPrefix}[ _]${namePattern}[ _]${breakpoint}${BREAKPOINT_END}`
      );
};

const getLevelProgressPercent = (
  groupPrefix: string | undefined,
  useFullNameAsGroupId: boolean,
  levelConfig: ILevelConfig,
  statusIds: Set<string>
) => {
  const breakpoints = getLevelBreakpoints(levelConfig);
  const ids = Array.from(statusIds);

  for (let i = breakpoints.length - 1; i >= 0; i--) {
    const breakpoint = breakpoints[i];
    const pattern = getGroupIdPattern(
      groupPrefix,
      useFullNameAsGroupId,
      levelConfig,
      breakpoint
    );

    if (ids.some((id) => pattern.test(id))) return breakpoint;
  }

  return -1;
};

interface IRewardGroupKeys {
  canceled: Set<string | undefined>;
  issued: Set<string | undefined>;
}

const hasRewardRecord = (
  levelConfig: ILevelConfig,
  rewardGroupKeys: IRewardGroupKeys
) =>
  rewardGroupKeys.issued.has(levelConfig.rewardGroupKey) ||
  rewardGroupKeys.canceled.has(levelConfig.rewardGroupKey);

const getLevelState = (
  levelConfig: ILevelConfig,
  matchedPercent: number,
  rewardGroupKeys: IRewardGroupKeys,
  isBonusDataPending: boolean
): LevelState => {
  if (matchedPercent !== 100)
    return matchedPercent >= 0 ? LevelState.Active : LevelState.Locked;

  // Bonuses/freespins haven't loaded yet - can't tell issued/canceled from
  // done, so hold at "locked" rather than flash a wrong state.
  if (isBonusDataPending) return LevelState.Locked;

  if (rewardGroupKeys.issued.has(levelConfig.rewardGroupKey))
    return LevelState.Issued;
  if (rewardGroupKeys.canceled.has(levelConfig.rewardGroupKey))
    return LevelState.Canceled;
  // No matching bonus record (not yet synced, or its stage already
  // expired/finished) is treated as done, not issued - there's no reward
  // left to claim, so no CTA should show.
  return LevelState.Done;
};

const LEVEL_PROGRESS_LABEL_KEY: Partial<Record<LevelState, string>> = {
  [LevelState.Locked]: 'missionLevels.card.locked',
  [LevelState.Canceled]: 'missionLevels.card.bonus',
};

const LEVEL_REWARD_LABEL_KEY: Partial<Record<LevelState, string>> = {
  [LevelState.Done]: 'missionLevels.card.done',
  [LevelState.Canceled]: 'missionLevels.card.canceled',
};

export const getMissionLevels = (
  groupPrefix: string | undefined,
  useFullNameAsGroupId: boolean,
  levelsConfig: ILevelConfig[],
  user?: User,
  rewardGroupKeys: IRewardGroupKeys = {
    canceled: new Set(),
    issued: new Set(),
  },
  isBonusDataPending = false,
  currencyCode?: string,
  locale?: string
): ILevelItem[] => {
  const statusIds = new Set((user?.statuses || []).map(({ id }) => id));
  const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode);

  const matchedPercents = levelsConfig.map((levelConfig) =>
    getLevelProgressPercent(
      groupPrefix,
      useFullNameAsGroupId,
      levelConfig,
      statusIds
    )
  );

  return levelsConfig.map((levelConfig, index) => {
    const id = levelConfig.name.toLowerCase();
    const target = getLevelTargetAmount(levelConfig);
    const rewardLabel = formatRewardLabel(
      levelConfig.reward,
      normalizedCurrencyCode,
      locale
    );
    const matchedPercent = matchedPercents[index];
    // Levels are sequential - if any later one already has a matched status,
    // this one must have been completed even without its own 100%/reward
    // record (e.g. skipped straight to a further group).
    const laterLevelStarted = matchedPercents
      .slice(index + 1)
      .some((laterPercent) => laterPercent >= 0);
    // The 100% status entry can be removed from user.statuses once its bonus
    // is issued/canceled, so matchedPercent alone would regress the level -
    // a reward record for the group is proof it reached 100% regardless.
    const effectivePercent =
      hasRewardRecord(levelConfig, rewardGroupKeys) ||
      (matchedPercent !== 100 && laterLevelStarted)
        ? 100
        : matchedPercent;
    const percent = clampToBreakpoint(effectivePercent);
    const wagered = getProgressAmount(levelConfig, percent);
    const state = getLevelState(
      levelConfig,
      effectivePercent,
      rewardGroupKeys,
      isBonusDataPending
    );

    return {
      id,
      state,
      percent,
      wagered,
      target,
      progress: formatProgressText(
        levelConfig,
        wagered,
        target,
        normalizedCurrencyCode
      ),
      progressLabelKey: LEVEL_PROGRESS_LABEL_KEY[state],
      reward: rewardLabel,
      rewardLabelKey: LEVEL_REWARD_LABEL_KEY[state],
      rewardLabel,
      rewardSuffixKey:
        levelConfig.reward.kind === 'bonus'
          ? levelConfig.reward.labelKey
          : undefined,
      image:
        state === LevelState.Locked
          ? levelConfig.lockedImage
          : levelConfig.image,
      glowColor: levelConfig.glowColor,
      glowEndOpacity: levelConfig.glowEndOpacity ?? 0.5,
    };
  });
};
